import type { ReactNode } from 'react';

import type { DangerLevel } from './danger';

/**
 * 弹窗服务（模块级单例）。
 *
 * 为什么是单例而不是某个组件的 `useState`：
 *   - 确认请求可能来自树上任意位置（地图上一个气泡、参数面板里一个按钮），
 *     而**只有一处**能渲染浮层（`<ConfirmDialogProvider>`），否则会出现两套弹窗样式；
 *   - `await confirm(...)` 的 Promise 必须活到用户作出选择，它的生命周期比任何一次渲染都长；
 *   - “Esc 关掉最上面那层”“还有几个没处理的确认”这类问题天然是全局的。
 *
 * 与 `overlayStack` 同一套取舍：几十行状态不值得引状态库，`useSyncExternalStore` 直接消费。
 *
 * **单宿主约定**：整个应用只应挂一处 `<ConfirmDialogProvider>`（GCS 挂在根节点）。
 * 刻意不做“多宿主选举”：两个宿主会让同一次请求被渲染成两份，
 * 而“悄悄挑一个宿主”会让另一个位置的界面看起来不响应 —— 宁可让它显式地只有一处。
 */

export type DialogEntryKind = 'confirm' | 'functional';

/** 按钮确认气泡：位置由 `anchor` 决定，视觉上从锚点飞出来 */
export interface ConfirmDialogOptions {
  readonly title: string;
  /** 后果。必填，且必须写清“会发生什么 + 能不能撤销”（渲染期由 `assertConfirmPolicy` 强制） */
  readonly consequence: string;
  readonly confirmLabel: string;
  readonly danger: DangerLevel;
  /** `critical` 必填：用户需要逐字输入才能确认 */
  readonly confirmPhrase?: string | undefined;
  readonly cancelLabel?: string | undefined;
  /** 补充信息（当前高度、目标航点等） */
  readonly detail?: ReactNode;
  /**
   * 触发按钮：既决定弹出位置（冻结于打开那一刻），也决定飞入/飞出的方向。
   * 通常是事件里的 `event.currentTarget`。
   *
   * **必填**（可以显式传 `null`）：这是刻意不让它可选 —— 忘了给锚点的后果是气泡跑到
   * 屏幕上方、失去"从按钮里长出来"的因果，而这种缺陷在界面上看起来"只是位置怪怪的"，
   * 不会有任何报错。传 `null` 就等于明说"我确实没有触发按钮"。
   */
  readonly anchor: Element | null;
}

/** 功能性弹窗：居中 + 模糊背景，无动画（它就是一块“临时页面”，不需要表演） */
export interface FunctionalDialogOptions {
  readonly title: string;
  /**
   * 内容。
   *
   * 注意：这是一个**创建时快照**的元素。调用方自己的 state 变化不会让这里重新渲染 ——
   * 需要跟着数据变的内容请传一个自己订阅数据源的组件（`<ParamTable />`），
   * 而不是在调用处拼一段依赖局部 state 的 JSX。
   */
  readonly content: ReactNode;
  /** 关闭时回调（Esc、closeAll、closeFunctionalDialog 都会走这里） */
  readonly onClose?: (() => void) | undefined;
  readonly maxWidth?: number | undefined;
}

interface DialogEntryBase {
  readonly id: string;
}

export interface ConfirmDialogEntry extends DialogEntryBase {
  readonly kind: 'confirm';
  readonly options: ConfirmDialogOptions;
  /**
   * 用户已经作出选择、正在播收回动画。
   *
   * 单独记一笔是因为有两件事要分开：Promise 已经落定（调用方可以继续走了），
   * 而界面还在收尾。带着这一位的条目会在**下一个请求到来时立刻让位** ——
   * 否则在 140ms 内连按两次同一个按钮，会看见两个气泡同时挂着。
   */
  readonly closing: boolean;
}

export interface FunctionalDialogEntry extends DialogEntryBase {
  readonly kind: 'functional';
  readonly options: FunctionalDialogOptions;
}

export type DialogEntry = ConfirmDialogEntry | FunctionalDialogEntry;

/**
 * 宿主缺失时的错误文案。
 *
 * 刻意写得像一句人话（而不是“provider not found”）：这条错误是**配置期**错误，
 * 它只会在开发时出现，出现时必须直接告诉人“补哪一行”。
 */
export const DIALOG_HOST_MISSING_MESSAGE =
  '未找到弹窗宿主：请在应用根部包一层 <ConfirmDialogProvider>（来自 @hgc/ui），再用 useConfirm() / useDialog()。' +
  '直接在 <ConfirmDialogProvider> 之外调用会静默失败，所以这里选择直接报错。';

const EMPTY_ENTRIES: readonly DialogEntry[] = [];

let entries: readonly DialogEntry[] = EMPTY_ENTRIES;
let sequence = 0;
const listeners = new Set<() => void>();

/**
 * 等待用户选择的确认请求：`id → resolve`。
 * 与条目分开存：条目是“界面上有什么”（纯数据、要能被 React 比较），
 * 这里的 resolver 是控制流，混在一起会让条目无法被冻结/比较。
 */
const pendingConfirms = new Map<string, (value: boolean) => void>();

/**
 * 宿主是否已挂载。
 *
 * 由 `<ConfirmDialogProvider>` 在**渲染期**登记（而不是 effect 里）：子组件可能在
 * 布局阶段就调用 `confirm()` —— 那比宿主的订阅 effect 更早 —— 而“有没有宿主”必须在那时
 * 已经有答案。登记只是置一个布尔标记、幂等，因此渲染期写入是安全的。
 */
let hostMounted = false;

export function registerDialogHost(): void {
  hostMounted = true;
}

export function unregisterDialogHost(): void {
  hostMounted = false;
}

export function isDialogHostMounted(): boolean {
  return hostMounted || listeners.size > 0;
}

/** 供 `useConfirm` / `useDialog` 在调用点立刻报错，而不是静默丢弃这次请求 */
export function assertDialogHost(): void {
  if (!isDialogHostMounted()) throw new Error(DIALOG_HOST_MISSING_MESSAGE);
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeDialogs(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    // 最后一个宿主消失 = 再也没有人能回答用户的选择。这时必须让等待中的确认以 `false`
    // 落定：否则 `await confirm(...)` 会永远挂起，调用点后面的代码再也不执行 ——
    // 表现为“点了按钮，什么都没发生”，比报错难查得多。
    if (listeners.size === 0) cancelPendingConfirms();
  };
}

/** 订阅者数量（宿主数量 + 直接订阅者）。测试用它断言“卸载后没有残留” */
export function listenerCount(): number {
  return listeners.size;
}

/** 供 `useSyncExternalStore` 用：只在真正增删时替换数组 */
export function getDialogs(): readonly DialogEntry[] {
  return entries;
}

function nextId(prefix: string): string {
  sequence += 1;
  return `${prefix}-${sequence}`;
}

/**
 * 请求一次按钮确认。返回的 Promise 在用户选择时落定（确认 `true`，取消/Esc/点击外部 `false`）。
 * 未挂载宿主时抛错：静默返回 `false` 会被调用点当成“用户点了取消”。
 */
export function requestConfirm(options: ConfirmDialogOptions): Promise<boolean> {
  assertDialogHost();
  const id = nextId('confirm');
  return new Promise<boolean>((resolve) => {
    pendingConfirms.set(id, resolve);
    // 正在收尾的旧气泡立刻让位：一个锚点上同时挂两个气泡会让人以为"弹了两下"
    const rest = entries.filter((entry) => !(entry.kind === 'confirm' && entry.closing));
    entries = [...rest, { kind: 'confirm', id, options, closing: false }];
    emit();
  });
}

/** 打开一个功能性弹窗（居中 + 模糊背景），返回它的 id（用于程序化关闭） */
export function openFunctionalDialog(options: FunctionalDialogOptions): string {
  assertDialogHost();
  const id = nextId('dialog');
  entries = [...entries, { kind: 'functional', id, options }];
  emit();
  return id;
}

/**
 * 用户已作出选择：Promise **立刻**落定，不等关闭动画。
 * 让调用方为 140ms 的视觉效果多等一等没有任何好处；条目仍留在栈上，
 * 由宿主在收回动画播完后调用 `removeDialogEntry`。
 */
export function settleConfirmDialog(id: string, confirmed: boolean): void {
  const resolve = pendingConfirms.get(id);
  if (resolve !== undefined) {
    pendingConfirms.delete(id);
    resolve(confirmed);
  }
  // 即使 Promise 已经落定过（重复调用），也要保证条目被标成"正在收回"：
  // 那一位同时是宿主的动画依据与"新请求来了就让位"的依据。
  if (!entries.some((entry) => entry.kind === 'confirm' && entry.id === id && !entry.closing)) return;
  entries = entries.map((entry) =>
    entry.kind === 'confirm' && entry.id === id ? { ...entry, closing: true } : entry,
  );
  emit();
}

export function removeDialogEntry(id: string): void {
  const next = entries.filter((entry) => entry.id !== id);
  if (next.length === entries.length) return;
  entries = next;
  emit();
}

/**
 * 关闭一个功能性弹窗。
 *
 * 功能性弹窗**没有**关闭动画（需求明确不要求），所以这里立即移除并立刻回调 ——
 * 提示“即时”是这个视觉档位的刻意选择，不是偷懒。
 */
export function closeFunctionalDialog(id: string): void {
  const entry = entries.find((candidate) => candidate.id === id);
  if (entry === undefined || entry.kind !== 'functional') return;
  removeDialogEntry(id);
  entry.options.onClose?.();
}

/**
 * 关闭所有弹窗。
 *
 * 确认类一律以 `false` 落定：这是一条安全方向的默认值 ——
 * 批量关闭通常由链路断开、切换机体这类事件触发，那种时刻**绝不能**被当成“用户答应了”。
 *
 * 与 Esc 的差别：这里**立即**摘掉确认气泡（不放收回动画）。批量关闭发生在"上下文已经换了"
 * 的时刻（断链、切机），这时还留一个正在淡出的旧气泡，反而像是新上下文的界面。
 */
export function closeAllDialogs(): void {
  // 先复制：`onClose` 是调用方的代码，它可能同步地改到弹窗栈
  for (const entry of [...entries]) {
    if (entry.kind === 'confirm') settleConfirmDialog(entry.id, false);
    else closeFunctionalDialog(entry.id);
  }
  for (const entry of [...entries]) removeDialogEntry(entry.id);
}

/**
 * 清空所有状态（测试隔离、整个界面重建）。
 * 与 `closeAllDialogs` 的差别：它不调用 `onClose`，只保证没人再挂在那里。
 */
export function disposeDialogs(): void {
  cancelPendingConfirms();
  entries = EMPTY_ENTRIES;
  emit();
}

function cancelPendingConfirms(): void {
  if (pendingConfirms.size === 0) return;
  const resolvers = [...pendingConfirms.values()];
  pendingConfirms.clear();
  for (const resolve of resolvers) resolve(false);
}
